import { LogoSetieca } from "@/componentes/marca/LogoSetieca"

/**
 * Las piezas de texto que se repiten en todo lo que la agencia imprime.
 *
 * ⚠ **Existen para que todos los formatos tengan la misma voz.** En el `.xls` que
 * esto sustituye cada hoja tiene su propia idea de cómo se escribe el nombre de un
 * campo —una en negrita, otra centrada, otra con dos puntos— porque cada hoja la
 * hizo alguien distinto un día distinto. Aquí un rotulillo es un rotulillo, y
 * cambiar su tamaño los cambia los cuatro.
 *
 * ⚠ **Los tamaños van en `mm` y no en `rem`.** Un `rem` depende del tamaño de fuente
 * del navegador de quien imprime; un milímetro es un milímetro. Es la misma razón por
 * la que existe `medidas.ts`.
 */

/**
 * El encabezado de un bloque: qué es esta pieza, con el filete dorado debajo.
 *
 * ⚠ **El filete corto es lo que ata el rótulo a la firma.** Es el mismo dorado del
 * borde del panel, en horizontal y en pequeño; sin él el bloque de texto se queda
 * flotando sobre el papel y la pieza parece dos diseños pegados.
 */
export function Titulillo({
  children,
  ancho = 22,
  tamano = 2.6,
}: {
  children: string
  ancho?: number
  /** En mm. El carnet lo pide más pequeño: con 2,6 el título chocaba con la ola. */
  tamano?: number
}) {
  return (
    <div>
      <p
        style={{
          margin: 0,
          fontSize: `${tamano}mm`,
          fontWeight: 700,
          letterSpacing: "0.45mm",
          textTransform: "uppercase",
          color: "var(--impreso-vino)",
        }}
      >
        {children}
      </p>
      <div
        style={{
          height: "0.5mm",
          width: `${ancho}mm`,
          marginTop: "1.5mm",
          background:
            "linear-gradient(90deg, var(--impreso-oro-2), var(--impreso-oro-3), var(--impreso-oro-4))",
        }}
      />
    </div>
  )
}

/**
 * El logotipo en tinta blanca, para el panel y la banda.
 *
 * ⚠ **Es el vector de la marca, no el logo que se sube en «Empresa».** Son dos cosas
 * distintas y conviene no confundirlas: el de «Empresa» es el MEMBRETE —quién firma
 * el documento, con su razón social y su RIF al lado— y por eso la carátula se lo
 * sigue pidiendo a `EncabezadoEmpresa`, que es el único sitio que lo compone. Éste es
 * la marca como elemento gráfico sobre el panel de color, igual que en la firma de
 * los correos: sobre el vino hace falta tinta blanca, y un PNG subido por alguien
 * trae sus colores dentro. En vectorial el color lo pone quien lo usa.
 */
export function MarcaBlanca({ alto, opacidad = 1 }: { alto: number; opacidad?: number }) {
  return (
    <LogoSetieca
      style={{
        height: `${alto}mm`,
        width: "auto",
        color: "var(--impreso-papel)",
        opacity: opacidad,
      }}
    />
  )
}

/**
 * El cuerpo más grande al que el texto sigue cabiendo de ancho, con su tope.
 *
 * Lo usa el lomo de una carpeta: una caja estrecha donde el texto lo pone quien
 * rellena la ficha. El carnet lo usó hasta que pasó a una letra estrecha y a un
 * nombre en dos líneas fijas; ahora mide con `carnet/tipografia.ts`.
 *
 * ⚠ **Mide la PALABRA más larga, no el texto entero.** Lo que decide si hay que
 * bajar el cuerpo no es el largo total —eso se resuelve envolviendo— sino que quepa
 * la palabra que no se puede partir. Sin esto hubo que poner `overflow-wrap: anywhere`
 * y el lomo salía «COMPR / AS Y / ADUANA / S»: partido por letras, que es justo lo que
 * `.claude/rules/frontend.md` prohíbe y por el mismo motivo — un texto partido a mitad
 * de palabra deja de leerse de un vistazo, que es lo único que un lomo tiene que
 * permitir.
 *
 * ⚠ **0,78 es el ancho de una versal de Montserrat Bold, medido y no supuesto.** El
 * primer intento fue 0,62 —el ancho medio de una minúscula— y «SIGLAS» se salía del
 * panel por los dos lados. Las versales de esta familia son anchas, y además el
 * `letter-spacing` suma: por eso se descuenta aparte.
 */
export function cuerpoQueCabe(texto: string, anchoUtil: number, tope: number): number {
  const palabra = texto.split(/\s+/).reduce((larga, actual) => Math.max(larga, actual.length), 1)
  const disponible = anchoUtil - 0.4 * (palabra - 1)
  return Math.max(2.5, Math.min(tope, disponible / (palabra * 0.78)))
}
